import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../config/msalConfig";
import { tokenService, TokenExchangeResponse } from "../services/tokenService";
import { authService } from "../services/authService";
import { CurrentUserModel } from "../models/ProfileModel";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: CurrentUserModel | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  unauthorizedError: UnauthorizedErrorType | null;
}

interface UnauthorizedErrorType {
  message: string;
  type: "unregistered" | "deactivated" | "inactive" | "unauthorized";
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticatedMsal = useIsAuthenticated();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<CurrentUserModel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unauthorizedError, setUnauthorizedError] =
    useState<UnauthorizedErrorType | null>(null);

  /**
   * Initialize authentication state on mount
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // IMPORTANT: Keep loading state true during MSAL redirect/login
        // This prevents the app from rendering PublicRoutes during the redirect
        if (inProgress !== InteractionStatus.None) {
          console.log(
            `[AUTH CONTEXT] MSAL interaction in progress: ${inProgress}, keeping loading state`
          );
          return; // Don't set isLoading to false
        }

        // Check if we have valid JWT tokens
        if (tokenService.hasValidTokens()) {
          const currentUser = authService.getCurrentUser();
          if (currentUser && currentUser.userId) {
            setUser(currentUser);
            setIsAuthenticated(true);
            setIsLoading(false);
            return;
          }
        }

        // If MSAL is authenticated but we don't have JWT tokens, exchange them
        if (isAuthenticatedMsal && inProgress === InteractionStatus.None) {
          await exchangeTokens();
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
        setError(err instanceof Error ? err.message : "Authentication failed");
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        // Only set loading to false if MSAL interaction is complete
        if (inProgress === InteractionStatus.None) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();
  }, [isAuthenticatedMsal, inProgress]);

  /**
   * Exchange Azure AD token for app JWT
   */
  const exchangeTokens = async () => {
    try {
      const accounts = instance.getAllAccounts();
      if (accounts.length === 0) {
        throw new Error("No accounts found");
      }

      // Get Azure AD access token
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      // Exchange with backend
      const tokenExchangeResponse: TokenExchangeResponse =
        await tokenService.exchangeToken(response.accessToken);

      // Store JWT tokens
      tokenService.storeTokens(tokenExchangeResponse.token);

      // Store user info
      const currentUser: CurrentUserModel = {
        userId: tokenExchangeResponse.user.id,
        identityId: tokenExchangeResponse.user.identityId,
        name: tokenExchangeResponse.user.name,
        email: tokenExchangeResponse.user.email,
        userType: tokenExchangeResponse.user.userType,
        isWhoAdmin: tokenExchangeResponse.user.isWhoAdmin,
        isActive: tokenExchangeResponse.user.isActive,
        isDeactivated: tokenExchangeResponse.user.isDeactivated,
        isNewUser: tokenExchangeResponse.user.isNewUser || false,
        scheme: "AzureAD",
        trackingId: "", // Will be set by analytics
      };

      authService.setCurrentUser(currentUser);
      setUser(currentUser);
      setIsAuthenticated(true);
      setError(null);
    } catch (err: any) {
      console.error("Token exchange error:", err);

      // Check if this is an unauthorized error from the API
      const apiError = err.response?.data;

      // Determine error type based on API response
      let errorType:
        | "unregistered"
        | "deactivated"
        | "inactive"
        | "unauthorized" = "unauthorized";
      let displayMessage =
        "Access to this website is restricted. To request access, please visit the landing page and select REGISTER to complete the registration process. If you have already registered and received confirmation, please reach out to the administrator SII.info@who.int";

      if (apiError?.error) {
        displayMessage = apiError.error;

        // Determine error type from API response
        if (
          displayMessage.toLowerCase().includes("not found") ||
          displayMessage.toLowerCase().includes("not registered")
        ) {
          errorType = "unregistered";
        } else if (displayMessage.toLowerCase().includes("deactivated")) {
          errorType = "deactivated";
        } else if (displayMessage.toLowerCase().includes("inactive")) {
          errorType = "inactive";
        }
      }

      // Store unauthorized error for routing
      setUnauthorizedError({
        message: displayMessage,
        type: errorType,
      });

      setError(displayMessage);

      // Clear tokens on error
      tokenService.clearTokens();
      localStorage.removeItem("userInfo");
      setIsAuthenticated(false);
      setUser(null);

      throw err;
    }
  };

  /**
   * Login with MSAL redirect
   * Using redirect flow instead of popup to avoid popup blocker issues
   * and redirect URI problems with single-page applications
   */
  const login = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Login with MSAL redirect (more reliable than popup)
      // After successful login, user will be redirected back to the app
      // and the token exchange will happen in the useEffect hook
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error("Login error:", err);
      setError(err instanceof Error ? err.message : "Login failed");
      setIsLoading(false);
      throw err;
    }
    // Note: setIsLoading(false) is not called here because the page will redirect
  };

  /**
   * Logout from MSAL and clear tokens
   */
  const logout = async () => {
    try {
      setIsLoading(true);

      // Revoke refresh token on backend
      const refreshToken = tokenService.getRefreshToken();
      if (refreshToken) {
        await tokenService.logout(refreshToken);
      }

      // Clear JWT tokens and user info
      tokenService.clearTokens();
      localStorage.removeItem("userInfo");
      sessionStorage.clear();

      // Logout from MSAL using redirect
      await instance.logoutRedirect();

      setIsAuthenticated(false);
      setUser(null);
      setError(null);
    } catch (err) {
      console.error("Logout error:", err);
      // Even if logout fails, clear local state
      tokenService.clearTokens();
      localStorage.removeItem("userInfo");
      sessionStorage.clear();
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    error,
    unauthorizedError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
